import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import { tagKeys } from '@/shared/lib/query/queryKeys';

import { useDomainHierarchy } from '@/entities/domain';
import type { TagCategoryItem } from '@/entities/tags';

import { updateTagCategoryDomains } from '@/features/tags/api';

type UseTagDomainEditParams = {
  tagCategoryId: string;
  categoryName: string;
};

export const useTagDomainEdit = ({ tagCategoryId, categoryName }: UseTagDomainEditParams) => {
  const queryClient = useQueryClient();
  const { data: hierarchyData } = useDomainHierarchy({ limit: 3000 });

  const tagCategoryData = queryClient.getQueryData<TagCategoryItem>(tagKeys.categoryDetail(categoryName));
  const currentDomains = useMemo(() => tagCategoryData?.domains || [], [tagCategoryData?.domains]);

  const updateDomainsMutation = useMutation({
    mutationFn: async (selectedDomainId: string | null) => {
      if (!hierarchyData?.data) {
        throw new Error(NOTIFICATION_MESSAGES.DOMAIN_TREE_UPDATE_ERROR);
      }
      await updateTagCategoryDomains(tagCategoryId, selectedDomainId, hierarchyData.data, currentDomains);
    },

    onError: (_error, _variables) => {
      notification.error({
        message: NOTIFICATION_MESSAGES.DOMAIN_TREE_UPDATE_ERROR,
        description: NOTIFICATION_MESSAGES.DOMAIN_TREE_UPDATE_ERROR_DESC,
        duration: 3,
        placement: 'bottomLeft',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.categoryDetail(categoryName) });
      notification.success({
        message: NOTIFICATION_MESSAGES.DOMAIN_UPDATE_SUCCESS,
        description: NOTIFICATION_MESSAGES.DOMAIN_UPDATE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });

  const handleSave = useCallback(
    async (selectedDomainIds: string[]) => {
      if (selectedDomainIds.length > 1) {
        notification.error({
          message: NOTIFICATION_MESSAGES.DOMAIN_TREE_UPDATE_ERROR,
          description: NOTIFICATION_MESSAGES.DOMAIN_TREE_UPDATE_ERROR_DESC,
          duration: 5,
          placement: 'bottomLeft',
        });
        return;
      }

      const selectedDomainId = selectedDomainIds.length > 0 ? selectedDomainIds[0] : null;
      await updateDomainsMutation.mutateAsync(selectedDomainId);
    },
    [updateDomainsMutation]
  );

  return {
    handleSave,
    isUpdating: updateDomainsMutation.isPending,
  };
};
